import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { AlertTriangle, Clock, Calendar, ChevronDown, ChevronUp } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Emlak modülü için Tahsis/Kira bitiş hatırlatma bandı.
 * Üst kısımda özet ozet, tıklayınca açılan/kapanan kayıt listesi.
 */
export default function EmlakExpiryBanner({ onSelectEmlak, refreshKey = 0 }) {
  const [data, setData] = useState({ items: [], counts: { total: 0 } });
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${API}/emlak-islemleri/expiry-reminders`, {
          withCredentials: true,
          params: { days_window: 90 }
        });
        if (!cancelled) setData(res.data || { items: [], counts: { total: 0 } });
      } catch (err) {
        console.error('Expiry reminders fetch failed', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => { cancelled = true; };
  }, [refreshKey]);

  if (loading) return null;
  if (data.counts.total === 0) return null;

  const { expired = 0, critical = 0, warning = 0, info = 0, total = 0 } = data.counts;

  const sevStyles = {
    expired: { bg: 'bg-red-950/40', border: 'border-red-700/60', text: 'text-red-300', dot: 'bg-red-500' },
    critical: { bg: 'bg-orange-950/40', border: 'border-orange-700/60', text: 'text-orange-300', dot: 'bg-orange-500' },
    warning: { bg: 'bg-amber-950/40', border: 'border-amber-700/60', text: 'text-amber-300', dot: 'bg-amber-500' },
    info: { bg: 'bg-blue-950/30', border: 'border-blue-700/40', text: 'text-blue-300', dot: 'bg-blue-500' },
  };

  const formatDays = (d) => {
    if (d < 0) return `${Math.abs(d)} gün geçti`;
    if (d === 0) return 'Bugün son gün!';
    if (d === 1) return 'Yarın sona eriyor';
    return `${d} gün kaldı`;
  };

  const formatDate = (iso) => {
    if (!iso) return '-';
    try {
      const [y, m, d] = iso.split('-');
      return `${d}.${m}.${y}`;
    } catch (e) { return iso; }
  };

  const totalSev = sevStyles[expired > 0 ? 'expired' : (critical > 0 ? 'critical' : (warning > 0 ? 'warning' : 'info'))];

  return (
    <div className={`rounded-lg border ${totalSev.border} ${totalSev.bg} overflow-hidden`} data-testid="emlak-expiry-banner">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-black/20 transition-colors"
        data-testid="emlak-expiry-banner-toggle"
      >
        <div className="flex items-center gap-3">
          <AlertTriangle className={`w-5 h-5 ${totalSev.text}`} />
          <div className="text-left">
            <div className="text-sm font-semibold text-white">
              Tahsis / Kira Bitiş Hatırlatmaları
            </div>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              {expired > 0 && <span className="text-[11px] bg-red-900/60 text-red-200 px-2 py-0.5 rounded-full">{expired} gecikmiş</span>}
              {critical > 0 && <span className="text-[11px] bg-orange-900/60 text-orange-200 px-2 py-0.5 rounded-full">{critical} kritik (7 gün)</span>}
              {warning > 0 && <span className="text-[11px] bg-amber-900/60 text-amber-200 px-2 py-0.5 rounded-full">{warning} yaklaşan (30 gün)</span>}
              {info > 0 && <span className="text-[11px] bg-blue-900/60 text-blue-200 px-2 py-0.5 rounded-full">{info} bilgi (90 gün)</span>}
              <span className="text-[11px] text-gray-400">· Toplam {total} kayıt</span>
            </div>
          </div>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
      </button>

      {open && (
        <div className="border-t border-white/10 max-h-96 overflow-y-auto" data-testid="emlak-expiry-banner-list">
          {data.items.map((it, idx) => {
            const sev = sevStyles[it.severity] || sevStyles.info;
            return (
              <button
                key={`${it.emlak_id}-${it.expiry_type}-${idx}`}
                type="button"
                onClick={() => onSelectEmlak && onSelectEmlak(it.emlak_id)}
                className="w-full flex items-start gap-3 px-4 py-3 border-b border-white/5 hover:bg-white/5 transition-colors text-left"
                data-testid={`expiry-item-${idx}`}
              >
                <span className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${sev.dot}`} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white truncate">{it.baslik}</div>
                  <div className="text-xs text-gray-400 mt-0.5 flex items-center gap-2 flex-wrap">
                    <span className="inline-flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {it.expiry_label}: {formatDate(it.expiry_date)}
                    </span>
                    <span className="text-gray-600">·</span>
                    <span className={`inline-flex items-center gap-1 font-semibold ${sev.text}`}>
                      <Clock className="w-3 h-3" />
                      {formatDays(it.days_remaining)}
                    </span>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
